import { BellIcon } from "@heroicons/react/24/outline"
import { ArrowRightIcon } from "@heroicons/react/24/solid"
import { useQuery } from "@tanstack/react-query"
import { Badge, Dropdown } from "antd"
import { Skeleton } from "components/new-stuff/ui/Skeleton"
import {
  formatReleaseDate,
  isWhatsNewEnabled,
  useFeatureAnnouncements,
  useUnseenFeatureAnnouncementCount,
  useUnseenFeatureAnnouncements,
} from "features/whats-new/useWhatsNew"
import { ViewNotificationDetail } from "pages/notifications/modals/ViewNotificationDetail"
import {
  CategoryChip,
  CategoryTile,
} from "pages/whats-new/components/categories"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { EMPLOYEE_ENDPOINTS, employeeService } from "services/employeeService"
import { IFeatureAnnouncement } from "services/featureAnnouncementsService"

type Props = {
  /** The Notifications page for this layout, e.g. "/dashboard/notifications". */
  notificationsPath: string
  /** The What's New page for this layout, e.g. "/dashboard/whats-new". */
  whatsNewPath: string
}

type Tab = "notifications" | "whatsnew"

const PREVIEW_SIZE = 5

const isRead = (it: any) => it?.is_read === true

/** Either tab's rows while they load: the icon tile beside a title and a date. */
const RowsSkeleton = () => (
  <ul aria-busy="true">
    {[62, 78, 54].map((width) => (
      <li
        key={width}
        className="flex items-start gap-3 border-b border-neutral20 px-5 py-3.5"
      >
        <Skeleton
          variant="block"
          width="36px"
          height="36px"
          className="shrink-0 !rounded-[10px]"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
          <Skeleton width={`${width}%`} />
          <Skeleton width="96px" />
        </div>
      </li>
    ))}
  </ul>
)

/**
 * The top bar's bell, shared by the admin, employee and manager layouts. One badge
 * counts unread notifications and unseen What's New posts; the dropdown keeps them on
 * separate tabs. What's New posts stay counted until the What's New page is opened.
 */
export const NotificationsBell = ({
  notificationsPath,
  whatsNewPath,
}: Props) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>("notifications")
  const [itemToView, setItemToView] = useState<string | null>(null)
  const [itemIsRead, setItemIsRead] = useState(false)

  const notificationsQuery = useQuery<IBaseRes<IPaginatedPayload<any>>>({
    queryKey: [EMPLOYEE_ENDPOINTS.SELECT_NOTIFICATIONS],
    queryFn: () =>
      employeeService.selectNotifications({ pageNumber: 1, pageSize: 100 }),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const unread: any[] =
    notificationsQuery.data?.data?.docs?.filter((it: any) => !isRead(it)) ?? []

  const whatsNewEnabled = isWhatsNewEnabled()
  const latest = useFeatureAnnouncements(1, PREVIEW_SIZE)
  const posts: IFeatureAnnouncement[] =
    latest.data?.data?.docs?.filter((post) => post.is_new) ?? []
  // The count includes posts kept out of the login popup. A backend without the count
  // endpoint falls back to the popup's list, so the badge never undercounts to zero.
  const { data: unseenCount, isSuccess: hasCount } =
    useUnseenFeatureAnnouncementCount()
  const { data: unseenPopup = [] } = useUnseenFeatureAnnouncements()
  const unseenPosts = !whatsNewEnabled
    ? 0
    : hasCount
    ? unseenCount ?? 0
    : unseenPopup.length

  const total = unread.length + unseenPosts

  const openDropdown = (next: boolean) => {
    // Land on whichever side has something new; notifications first.
    if (next) {
      setTab(
        unread.length === 0 && unseenPosts > 0 ? "whatsnew" : "notifications"
      )
    }
    setOpen(next)
  }

  const viewNotification = (item: any) => {
    setOpen(false)
    setItemIsRead(isRead(item))
    setItemToView(item?.employee_announcement_key)
  }

  const viewPost = (post: IFeatureAnnouncement) => {
    setOpen(false)
    navigate(`${whatsNewPath}?id=${post.id}`)
  }

  const tabClass = (active: boolean) =>
    `-mb-px flex items-center gap-2 border-b-2 px-1 pb-3 pt-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500 ${
      active
        ? "border-primary500 text-primary500"
        : "border-transparent text-neutral300 hover:text-neutral500"
    }`

  const countClass = (active: boolean) =>
    `inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
      active ? "bg-primary500 text-white" : "bg-neutral30 text-neutral500"
    }`

  const panel = (
    <div className="w-[calc(100vw-32px)] max-w-[400px] overflow-hidden rounded-2xl border border-neutral30 bg-white font-avenir shadow-[0_16px_40px_rgba(12,27,61,0.16)]">
      <div className="flex flex-col gap-3 px-5 pt-4">
        <div className="text-[17px] font-bold text-padeBlack">
          Notifications
        </div>
        {whatsNewEnabled && (
          <div
            role="tablist"
            className="flex gap-4 border-b border-neutral30"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "notifications"}
              onClick={() => setTab("notifications")}
              className={tabClass(tab === "notifications")}
            >
              Notifications
              <span className={countClass(tab === "notifications")}>
                {unread.length}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "whatsnew"}
              onClick={() => setTab("whatsnew")}
              className={tabClass(tab === "whatsnew")}
            >
              What's New
              <span className={countClass(tab === "whatsnew")}>
                {unseenPosts}
              </span>
            </button>
          </div>
        )}
      </div>

      {tab === "notifications" || !whatsNewEnabled ? (
        <>
          {notificationsQuery.isLoading ? (
            <RowsSkeleton />
          ) : unread.length > 0 ? (
            <ul className="flex flex-col">
              {unread.slice(0, PREVIEW_SIZE).map((item: any) => (
                <li key={item?.employee_announcement_key ?? item?.id}>
                  <button
                    type="button"
                    onClick={() => viewNotification(item)}
                    className="flex w-full items-start gap-3 border-b border-neutral20 bg-[#F7F9FF] px-5 py-3.5 text-left transition-colors hover:bg-primary50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary500"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary50 text-primary500">
                      <BellIcon className="w-[18px] stroke-2" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-semibold text-padeBlack">
                        {item?.subject}
                      </span>
                      {item?.created_date && (
                        <span className="text-[13px] text-padeSubtext">
                          {formatReleaseDate(item.created_date)}
                        </span>
                      )}
                    </span>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary500" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-8 text-center text-sm text-padeSubtext">
              You're all caught up. New notifications will appear here.
            </p>
          )}
          <div className="flex justify-center px-5 py-3.5">
            <Link
              to={notificationsPath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary500 hover:text-primary600"
            >
              View all notifications
              <ArrowRightIcon className="w-4" />
            </Link>
          </div>
        </>
      ) : (
        <>
          {latest.isLoading ? (
            <RowsSkeleton />
          ) : posts.length > 0 ? (
            <ul className="flex flex-col">
              {posts.map((post) => (
                <li key={post.id}>
                  <button
                    type="button"
                    onClick={() => viewPost(post)}
                    className={`flex w-full items-start gap-3 border-b border-neutral20 px-5 py-3.5 text-left transition-colors hover:bg-primary50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary500 ${
                      post.is_new ? "bg-[#F7F9FF]" : "bg-white"
                    }`}
                  >
                    <CategoryTile
                      item={post}
                      className="h-9 w-9 rounded-[10px]"
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="truncate text-sm font-semibold text-padeBlack">
                        {post.title}
                      </span>
                      <span className="flex items-center gap-2 text-xs text-padeSubtext">
                        <CategoryChip item={post} />
                        {formatReleaseDate(post.release_date)}
                      </span>
                    </span>
                    {post.is_new && (
                      <span className="shrink-0 rounded-full bg-primary500 px-2 py-0.5 text-[11px] font-bold text-white">
                        New
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-5 py-8 text-center text-sm text-padeSubtext">
              You're all caught up. New product updates will appear here.
            </p>
          )}
          <div className="flex justify-center px-5 py-3.5">
            <Link
              to={whatsNewPath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary500 hover:text-primary600"
            >
              See everything on What's New
              <ArrowRightIcon className="w-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      <Dropdown
        trigger={["click"]}
        open={open}
        onOpenChange={openDropdown}
        dropdownRender={() => panel}
        placement="bottomRight"
      >
        <button
          type="button"
          aria-label={
            total > 0 ? `Notifications, ${total} unread` : "Notifications"
          }
          className="flex cursor-pointer"
        >
          <Badge count={total || null}>
            <BellIcon className="w-6 stroke-2 text-primary500" />
          </Badge>
        </button>
      </Dropdown>
      {itemToView && (
        <ViewNotificationDetail
          onClose={() => {
            !itemIsRead && notificationsQuery.refetch()
            setItemToView(null)
          }}
          id={itemToView}
          itemIsRead={itemIsRead}
        />
      )}
    </>
  )
}
